import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconArrowRight } from '@tabler/icons-react';
import styles from '../ReportMetrics.module.scss';
import detailStyles from '../widgets/Detail.module.scss';
import { ActionPointDto, KpiScope } from '../../../api/model';
import {
  ACTION_STATE_TONE,
  AREAS,
  Area,
  ActionState,
  PRIORITY_TONE,
  TONE_COLOR,
  actionArea,
  actionState,
  actionSummary,
  actionTitle,
  actionsOverTime,
  byUrgency,
  isAutoClosed,
  isOpenAction,
  isRuleAction,
  isoDay,
} from '../detail';
import type { StatusDatum } from '../reportData';
import type { ReportTab } from '../ReportMetrics';
import type { ReportViewProps } from './viewProps';
import { formatDay, pct } from './tiles';
import ChartCard from '../widgets/ChartCard';
import MetricTile from '../widgets/MetricTile';
import DataTable from '../widgets/DataTable';
import StatusBadge from '../widgets/StatusBadge';
import StatusDonut from '../widgets/StatusDonut';
import CategoryBarChart from '../widgets/CategoryBarChart';
import WeeklyActionsChart from '../widgets/WeeklyActionsChart';
import Segmented from '../widgets/Segmented';
import { useTenantUsers } from '../../../hooks/useTenantUsers';

const STATES: ActionState[] = ['overdue', 'open', 'inProgress', 'done'];

/** Same blues as the task board for open / in progress, the status colours for the rest. */
const STATE_COLOR: Record<ActionState, string> = {
  overdue: TONE_COLOR.critical,
  open: '#6DA7EC',
  inProgress: '#2D78DB',
  done: TONE_COLOR.good,
  cancelled: TONE_COLOR.neutral,
};

const AREA_TAB: Record<Area, ReportTab> = {
  execution: 'execution',
  readiness: 'readiness',
  quality: 'quality',
  dependencies: 'dependencies',
};

type Filter = 'open' | 'overdue' | 'rule' | 'all';

/** The period of "Actions im Zeitverlauf", in weeks. */
type Period = '4' | '12' | '26' | '52';

/** "+25 %" against the period before; nothing to compare with when that one was empty. */
const changeText = (current: number, previous: number) => {
  if (previous === 0) return undefined;
  const change = Math.round(((current - previous) / previous) * 100);
  return change === 0 ? '±0 %' : `${change > 0 ? '+' : '−'}${Math.abs(change)} %`;
};

const FILTERS: Record<Filter, (p: ActionPointDto, today: string) => boolean> = {
  open: (p) => isOpenAction(p),
  overdue: (p, today) => actionState(p, today) === 'overdue',
  rule: (p) => isRuleAction(p),
  all: () => true,
};

/**
 * Actions (Detailmasken §7): what has to be done, by whom and by when.
 *
 * The Action Center's rules now raise Action Points themselves - owned by the
 * project lead, due by severity - and close them with the reason
 * "resolved automatically" once the finding is gone (customer, 2026-09-24).
 * Manually created ones sit in the same list. So the tiles, the timeline and
 * the work list all read one source: the project's Action Points.
 */
const Actions: React.FC<ReportViewProps> = ({ actionPoints, phaseNames, openTab, openSection, openObject }) => {
  const { t } = useTranslation(['templates', 'gantt']);
  const { namesByIds } = useTenantUsers();
  const today = isoDay(new Date());
  const [filter, setFilter] = useState<Filter>('open');
  const [period, setPeriod] = useState<Period>('12');

  const summary = useMemo(() => actionSummary(actionPoints), [actionPoints]);
  const ordered = useMemo(() => byUrgency(actionPoints), [actionPoints]);
  const listed = useMemo(() => ordered.filter((p) => FILTERS[filter](p, today)), [ordered, filter, today]);
  const [selectedId, setSelectedId] = useState<number | undefined>(undefined);
  const selected: ActionPointDto | undefined = listed.find((p) => p.id === selectedId) ?? listed[0];

  const byState: StatusDatum[] = useMemo(
    () =>
      STATES.map((state) => ({
        status: `action_${state}`,
        value: (actionPoints ?? []).filter((p) => actionState(p, today) === state).length,
        color: STATE_COLOR[state],
      })).filter((d) => d.value > 0),
    [actionPoints, today],
  );

  const byArea = useMemo(
    () =>
      AREAS.map((area) => ({
        label: t(`report.detail.area.${area}`),
        value: (actionPoints ?? []).filter((p) => isOpenAction(p) && actionArea(p) === area).length,
      })),
    [actionPoints, t],
  );

  const flow = useMemo(() => actionsOverTime(actionPoints, Number(period)), [actionPoints, period]);

  const owners = (point: ActionPointDto) =>
    namesByIds(point.assignedUserIds ? [...point.assignedUserIds] : []) || t('report.detail.actions.noOwner');
  const title = (point: ActionPointDto) => actionTitle(t, point, phaseNames);
  const filterCount = (f: Filter) => ordered.filter((p) => FILTERS[f](p, today)).length;

  const selectedArea = selected ? actionArea(selected) : undefined;

  return (
    <div className={styles.dashboard}>
      <div className={styles.tileRow}>
        <MetricTile label={t('report.detail.actions.tiles.open')} value={`${summary.open}`} />
        <MetricTile
          label={t('report.detail.actions.tiles.overdue')}
          value={`${summary.overdue}`}
          status={summary.overdue > 0 ? { tone: 'critical', label: t('report.severity.CRITICAL') } : undefined}
        />
        <MetricTile label={t('report.detail.actions.tiles.dueSoon')} value={`${summary.dueSoon}`} />
        <MetricTile
          label={t('report.detail.actions.tiles.done')}
          value={`${summary.done}`}
          sub={summary.autoClosed > 0 ? t('report.detail.actions.ofWhichAuto', { count: summary.autoClosed }) : undefined}
        />
        <MetricTile
          label={t('report.detail.actions.tiles.unassigned')}
          value={`${summary.unassigned}`}
          status={summary.unassigned > 0 ? { tone: 'warning', label: t('report.detail.attention') } : undefined}
        />
        <MetricTile
          label={t('report.detail.actions.tiles.completion')}
          value={pct(summary.completionRate)}
          bar={{ value: summary.completionRate, tone: 'good' }}
        />
      </div>

      <ChartCard
        title={t('report.detail.actions.overTime')}
        subtitle={t('report.detail.actions.overTimeHint')}
        action={
          <Segmented<Period>
            value={period}
            onChange={setPeriod}
            options={[
              { value: '4', label: t('report.detail.range.weeks4') },
              { value: '12', label: t('report.detail.range.weeks12') },
              { value: '26', label: t('report.detail.range.months6') },
              { value: '52', label: t('report.detail.range.months12') },
            ]}
          />
        }
        fitContent
      >
        <div className={detailStyles.milestoneSummary}>
          {([
            ['raised', flow.raised, flow.previousRaised],
            ['resolved', flow.resolved, flow.previousResolved],
          ] as const).map(([key, current, previous]) => {
            const change = changeText(current, previous);
            return (
              <div key={key}>
                <span className={detailStyles.tileLabel}>{t(`report.detail.actions.flow.${key}`)}</span>
                <span className={detailStyles.milestoneCount}>{current}</span>
                {change && (
                  <span className={detailStyles.note}>{t('report.detail.actions.flow.vsPrevious', { change })}</span>
                )}
              </div>
            );
          })}
          <div>
            <span className={detailStyles.tileLabel}>{t('report.detail.actions.flow.open')}</span>
            <span className={detailStyles.milestoneCount}>{summary.open}</span>
            {summary.overdue > 0 && (
              <span className={`${detailStyles.note} ${detailStyles.critical}`}>
                {t('report.detail.actions.flow.ofWhichOverdue', { count: summary.overdue })}
              </span>
            )}
          </div>
        </div>
        <div style={{ height: 280 }}>
          <WeeklyActionsChart weeks={flow.weeks} />
        </div>
        <p className={detailStyles.note} style={{ marginTop: 8 }}>{t('report.detail.actions.flow.note')}</p>
      </ChartCard>

      <div className={detailStyles.halves}>

        <ChartCard title={t('report.detail.actions.byStatus')} subtitle={t('report.detail.actions.byStatusHint')}>
          {byState.length > 0 ? (
            <StatusDonut data={byState} totalLabel={t('report.detail.actions.totalLabel')} />
          ) : (
            <p className={styles.planDescription}>{t('report.detail.actions.none')}</p>
          )}
        </ChartCard>

        <ChartCard title={t('report.detail.actions.byArea')} subtitle={t('report.detail.actions.byAreaHint')}>
          <CategoryBarChart data={byArea} seriesName={t('report.detail.actions.tiles.open')} />
        </ChartCard>
      </div>

      <div className={detailStyles.twoColumns}>
        <ChartCard
          title={t('report.detail.actions.list')}
          subtitle={t('report.detail.actions.listHint')}
          action={
            <button type="button" className={detailStyles.linkButton} onClick={() => openSection?.('task-action-points')}>
              {t('report.detail.actions.all')}
              <IconArrowRight size={14} stroke={2} />
            </button>
          }
          fitContent
        >
          <Segmented<Filter>
            value={filter}
            onChange={setFilter}
            options={(['open', 'overdue', 'rule', 'all'] as Filter[]).map((f) => ({
              value: f,
              label: `${t(`report.detail.actions.filter.${f}`)} (${filterCount(f)})`,
            }))}
          />
          <DataTable
            rows={listed}
            rowKey={(p) => p.id as number}
            limit={10}
            empty={t('report.detail.actions.none')}
            onRowClick={(p) => setSelectedId(p.id)}
            isSelected={(p) => p.id === selected?.id}
            columns={[
              { key: 'name', header: t('report.detail.col.title'), render: title },
              {
                key: 'area',
                header: t('report.detail.col.area'),
                render: (p) => {
                  const area = actionArea(p);
                  return area ? t(`report.detail.area.${area}`) : '—';
                },
              },
              {
                key: 'priority',
                header: t('report.detail.col.priority'),
                render: (p) =>
                  p.priority ? (
                    <StatusBadge tone={PRIORITY_TONE[p.priority]} label={t(`report.detail.priority.${p.priority}`)} />
                  ) : (
                    '—'
                  ),
              },
              {
                key: 'due',
                header: t('report.detail.col.due'),
                render: (p) => (
                  <span className={actionState(p, today) === 'overdue' ? detailStyles.critical : undefined}>
                    {formatDay(p.validationDate)}
                  </span>
                ),
              },
              {
                key: 'state',
                header: t('report.detail.col.status'),
                render: (p) => {
                  const state = actionState(p, today);
                  return <StatusBadge tone={ACTION_STATE_TONE[state]} label={t(`report.detail.actions.state.${state}`)} />;
                },
              },
              { key: 'owner', header: t('report.detail.col.owner'), render: owners },
            ]}
          />
        </ChartCard>

        <ChartCard
          title={t('report.detail.actions.detail')}
          action={
            selected?.id != null ? (
              <button
                type="button"
                className={detailStyles.linkButton}
                onClick={() => openObject?.({ kind: 'actionPoint', id: selected.id as number })}
              >
                {t('report.detail.actions.edit')}
                <IconArrowRight size={14} stroke={2} />
              </button>
            ) : undefined
          }
          fitContent
        >
          {selected ? (
            <dl className={detailStyles.facts}>
              <dt>{t('report.detail.col.title')}</dt>
              <dd>{title(selected)}</dd>
              <dt>{t('report.detail.col.status')}</dt>
              <dd>
                <StatusBadge
                  tone={ACTION_STATE_TONE[actionState(selected, today)]}
                  label={t(`report.detail.actions.state.${actionState(selected, today)}`)}
                />
                {selected.closedReason && (
                  <span className={detailStyles.tileSub}>
                    {' · '}
                    {t(`report.detail.actions.closedReason.${selected.closedReason}`)}
                    {selected.closedAt ? ` ${formatDay(selected.closedAt.slice(0, 10))}` : ''}
                  </span>
                )}
              </dd>
              <dt>{t('report.detail.col.priority')}</dt>
              <dd>{selected.priority ? t(`report.detail.priority.${selected.priority}`) : '—'}</dd>
              <dt>{t('report.detail.col.due')}</dt>
              <dd>{formatDay(selected.validationDate)}</dd>
              <dt>{t('report.detail.col.owner')}</dt>
              <dd>{owners(selected)}</dd>
              <dt>{t('report.detail.actions.source')}</dt>
              <dd>
                {isRuleAction(selected) ? (
                  <>
                    {t('report.detail.actions.fromRule')}
                    {selected.ruleActive && !isOpenAction(selected) && (
                      <span className={detailStyles.tileSub}> · {t('report.detail.actions.stillFiring')}</span>
                    )}
                  </>
                ) : selected.checkPointId != null ? (
                  <button type="button" className={detailStyles.linkButton} onClick={() => openSection?.('check-plan')}>
                    {t('report.detail.actions.fromCheckpoint', { id: selected.checkPointId })}
                    <IconArrowRight size={14} stroke={2} />
                  </button>
                ) : (
                  t('report.detail.actions.manual')
                )}
              </dd>
              {isRuleAction(selected) && (
                <>
                  <dt>{t('report.detail.col.object')}</dt>
                  <dd>
                    {selected.ruleScopeType === KpiScope.PHASE && selected.ruleScopeId != null ? (
                      <button
                        type="button"
                        className={detailStyles.linkButton}
                        onClick={() => selectedArea && openTab?.(AREA_TAB[selectedArea])}
                      >
                        {phaseNames.get(selected.ruleScopeId) ?? `#${selected.ruleScopeId}`}
                        <IconArrowRight size={14} stroke={2} />
                      </button>
                    ) : (
                      '—'
                    )}
                  </dd>
                </>
              )}
              {!isRuleAction(selected) && (
                <>
                  <dt>{t('report.detail.actions.criteria')}</dt>
                  <dd>{selected.validationCriteria || '—'}</dd>
                  <dt>{t('report.detail.actions.method')}</dt>
                  <dd>{selected.validationMethod || '—'}</dd>
                </>
              )}
              <dt>{t('report.detail.actions.comment')}</dt>
              <dd>{selected.comment || '—'}</dd>
            </dl>
          ) : (
            <div className={detailStyles.empty}>{t('report.detail.actions.selectHint')}</div>
          )}
          {selected && isAutoClosed(selected) && (
            <p className={detailStyles.note}>{t('report.detail.actions.autoClosedNote')}</p>
          )}
        </ChartCard>
      </div>
    </div>
  );
};

export default Actions;
